<template>
  <v-dialog v-model="dialogModel" max-width="800px" persistent>
    <v-card dark>
      <v-card-title>
        <span class="text-h5">{{
          isEditing ? "Edit Stall" : "New Stall"
        }}</span>
      </v-card-title>

      <v-card-text>
        <stall-form
          :form-data="localStall"
          :event-settings="stallholderSettings"
          :inheritance-states="inheritanceStates"
          :selected-event="selectedEvent"
          :selected-stallholder="selectedStallholder"
          :printing-list="printingList"
          :enforce-types="enforceTypes"
          :add-button-return-home-types="addButtonReturnHomeTypes"
          :order-number-types="orderNumberTypes"
          @update-field="handleFieldUpdate"
          @inheritance-change="handleInheritanceChange"
        />
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="blue darken-1" text @click="cancel"> Cancel </v-btn>
        <v-btn color="blue darken-1" @click="save"> Save </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import StallForm from "@/components/forms/StallForm.vue";

export default {
  name: "StallDialog",
  components: {
    StallForm,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    stall: {
      type: Object,
      required: true,
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
    selectedEvent: {
      type: Object,
      required: true,
    },
    selectedStallholder: {
      type: Object,
      required: true,
    },
    printingList: {
      type: Array,
      required: true,
    },
    enforceTypes: {
      type: Array,
      required: true,
    },
    addButtonReturnHomeTypes: {
      type: Array,
      required: true,
    },
    orderNumberTypes: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      localStall: {},
      inheritanceStates: {
        isSelectABN: true,
        isSelectPaymentProfile: true,
        isSelectPrintingProfile: true,
        isSelectStandardDrinkLimit: true,
        isSelectStandardDrinkLimitEnforce: true,
        isSelectCardSurchargePercentage: true,
      },
    };
  },
  computed: {
    dialogModel: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    stallholderSettings() {
      return {
        Abn: this.selectedStallholder.Abn || "",
        PaymentProfile: Array.isArray(this.selectedStallholder.PaymentProfile)
          ? this.selectedStallholder.PaymentProfile.join(", ")
          : this.selectedStallholder.PaymentProfile || "",
        PrintingProfile: this.selectedStallholder.PrintingProfile || "",
        StandardDrinkLimit: this.selectedStallholder.StandardDrinkLimit || "",
        StandardDrinkLimitEnforce:
          this.selectedStallholder.StandardDrinkLimitEnforce || "",
        CardSurchargePercentage:
          this.selectedStallholder.CardSurchargePercentage || "",
      };
    },
  },
  watch: {
    stall: {
      handler(newStall) {
        this.initializeLocalStall(newStall);
      },
      immediate: true,
      deep: true,
    },
    value(newVal) {
      if (newVal) {
        this.initializeLocalStall(this.stall);
      }
    },
  },
  methods: {
    initializeLocalStall(stall) {
      this.localStall = {
        EventId: stall.EventId || this.selectedEvent.EventId || "",
        StallholderId:
          stall.StallholderId || this.selectedStallholder.StallholderId || "",
        StallName: stall.StallName || "",
        StandardDrinkLimit: stall.StandardDrinkLimit || "",
        StandardDrinkLimitEnforce:
          stall.StandardDrinkLimitEnforce !== undefined
            ? stall.StandardDrinkLimitEnforce
            : "",
        PaymentProfile: stall.PaymentProfile || [],
        AddButtonReturnHome:
          stall.AddButtonReturnHome !== undefined
            ? stall.AddButtonReturnHome
            : "Yes",
        PrintingProfile: stall.PrintingProfile || "",
        OrderNumbers:
          stall.OrderNumbers !== undefined ? stall.OrderNumbers : "Yes",
        CardSurchargePercentage: stall.CardSurchargePercentage || "",
        ManagerPin: stall.ManagerPin || "",
        Abn: stall.Abn || "",
        AbnInherit: stall.AbnInherit !== undefined ? stall.AbnInherit : true,
        PaymentProfileInherit:
          stall.PaymentProfileInherit !== undefined
            ? stall.PaymentProfileInherit
            : true,
        PrintingProfileInherit:
          stall.PrintingProfileInherit !== undefined
            ? stall.PrintingProfileInherit
            : true,
        CardSurchargePercentageInherit:
          stall.CardSurchargePercentageInherit !== undefined
            ? stall.CardSurchargePercentageInherit
            : true,
        StandardDrinkLimitInherit:
          stall.StandardDrinkLimitInherit !== undefined
            ? stall.StandardDrinkLimitInherit
            : true,
        StandardDrinkLimitEnforceInherit:
          stall.StandardDrinkLimitEnforceInherit !== undefined
            ? stall.StandardDrinkLimitEnforceInherit
            : true,
      };

      // Set inheritance states based on the stall data
      this.inheritanceStates = {
        isSelectABN: this.localStall.AbnInherit,
        isSelectPaymentProfile: this.localStall.PaymentProfileInherit,
        isSelectPrintingProfile: this.localStall.PrintingProfileInherit,
        isSelectStandardDrinkLimit: this.localStall.StandardDrinkLimitInherit,
        isSelectStandardDrinkLimitEnforce:
          this.localStall.StandardDrinkLimitEnforceInherit,
        isSelectCardSurchargePercentage:
          this.localStall.CardSurchargePercentageInherit,
      };

      // Apply inheritance if flags are set
      this.applyInheritance();
    },
    applyInheritance() {
      if (this.inheritanceStates.isSelectABN) {
        this.localStall.Abn = this.stallholderSettings.Abn;
      }
      if (this.inheritanceStates.isSelectPaymentProfile) {
        const paymentProfile = this.stallholderSettings.PaymentProfile;
        this.localStall.PaymentProfile = paymentProfile
          ? paymentProfile.split(", ")
          : [];
      }
      if (this.inheritanceStates.isSelectPrintingProfile) {
        this.localStall.PrintingProfile =
          this.stallholderSettings.PrintingProfile;
      }
      if (this.inheritanceStates.isSelectStandardDrinkLimit) {
        this.localStall.StandardDrinkLimit =
          this.stallholderSettings.StandardDrinkLimit;
      }
      if (this.inheritanceStates.isSelectStandardDrinkLimitEnforce) {
        this.localStall.StandardDrinkLimitEnforce =
          this.stallholderSettings.StandardDrinkLimitEnforce;
      }
      if (this.inheritanceStates.isSelectCardSurchargePercentage) {
        this.localStall.CardSurchargePercentage =
          this.stallholderSettings.CardSurchargePercentage;
      }
    },
    handleFieldUpdate(field, value) {
      this.localStall[field] = value;
    },
    handleInheritanceChange(fieldKey, inheritKey, useStallholderSetting) {
      this.inheritanceStates[
        inheritKey
          .replace("Inherit", "")
          .replace(/^(\w)/, (match, p1) => "isSelect" + p1.toUpperCase())
      ] = useStallholderSetting;
      this.localStall[inheritKey] = useStallholderSetting;

      if (useStallholderSetting) {
        // Apply stallholder setting
        switch (fieldKey) {
          case "Abn":
            this.localStall.Abn = this.stallholderSettings.Abn;
            break;
          case "PaymentProfile": {
            const paymentProfile = this.stallholderSettings.PaymentProfile;
            this.localStall.PaymentProfile = paymentProfile
              ? paymentProfile.split(", ")
              : [];
            break;
          }
          case "PrintingProfile":
            this.localStall.PrintingProfile =
              this.stallholderSettings.PrintingProfile;
            break;
          case "StandardDrinkLimit":
            this.localStall.StandardDrinkLimit =
              this.stallholderSettings.StandardDrinkLimit;
            break;
          case "StandardDrinkLimitEnforce":
            this.localStall.StandardDrinkLimitEnforce =
              this.stallholderSettings.StandardDrinkLimitEnforce;
            break;
          case "CardSurchargePercentage":
            this.localStall.CardSurchargePercentage =
              this.stallholderSettings.CardSurchargePercentage;
            break;
        }
      }
    },
    save() {
      // Convert string values back to appropriate types for submission
      const stallData = { ...this.localStall };

      // Convert string boolean values to actual booleans
      if (typeof stallData.StandardDrinkLimitEnforce === "string") {
        if (stallData.StandardDrinkLimitEnforce === "Enforce") {
          stallData.StandardDrinkLimitEnforce = true;
        } else if (stallData.StandardDrinkLimitEnforce === "Prompt") {
          stallData.StandardDrinkLimitEnforce = false;
        }
      }

      if (typeof stallData.OrderNumbers === "string") {
        stallData.OrderNumbers = stallData.OrderNumbers === "Yes";
      }

      if (typeof stallData.AddButtonReturnHome === "string") {
        stallData.AddButtonReturnHome = stallData.AddButtonReturnHome === "Yes";
      }

      this.$emit("save", stallData);
    },
    cancel() {
      this.$emit("cancel");
    },
  },
};
</script>
